import {
  isPlaybookSectionNode,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { FC, ReactNode } from "react";

export interface FeedbackReviewNodePanelProps {
  /** The criterion or section being reviewed; undefined for an empty playbook. */
  node: PlaybookNode | undefined;
  /**
   * The node's place in the review queue ("Criterion 2 of 5 open"); null when
   * it has no open feedback.
   */
  openFeedbackPosition: { position: number; total: number } | null;
}

/**
 * The review tab's left panel: what the grader was told to do for the selected
 * node. Read-only on purpose — the playbook is edited on the Playbook tab, and
 * this is the reference a reviewer reads the feedback against.
 */
export const FeedbackReviewNodePanel: FC<FeedbackReviewNodePanelProps> = (
  props,
) => {
  const { node, openFeedbackPosition } = props;

  if (!node) {
    return (
      <div className="flex min-h-0 flex-col rounded-lg border border-border-light bg-white p-4">
        <p className="text-sm text-muted-foreground">
          This playbook has no criteria yet.
        </p>
      </div>
    );
  }

  const isSection = isPlaybookSectionNode(node);

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto rounded-lg border border-border-light bg-white p-4">
      <p className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-foreground">
          {isSection ? "Section" : "Criterion"}
        </span>
        {openFeedbackPosition ? (
          <span className="text-xs text-muted-foreground">
            {openFeedbackPosition.position} of {openFeedbackPosition.total} open
          </span>
        ) : null}
      </p>
      <h2 className="mt-1 text-base font-semibold text-foreground">
        {node.name}
      </h2>

      <p className="mt-4 mb-1 text-xs font-medium text-muted-foreground">
        {isSection ? "Description" : "Grading context"}
      </p>
      <NodeText
        value={isSection ? node.description : node.grading_context}
        emptyMessage={
          isSection
            ? "No description for this section."
            : "No grading context for this criterion."
        }
      />
    </div>
  );
};

interface NodeTextProps {
  value: string | undefined;
  emptyMessage: ReactNode;
}

const NodeText: FC<NodeTextProps> = (props) => {
  const { value, emptyMessage } = props;

  if (!value?.trim()) {
    return (
      <p className="text-sm text-muted-foreground italic">{emptyMessage}</p>
    );
  }

  return <p className="text-sm whitespace-pre-wrap text-foreground">{value}</p>;
};
